import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  EVDrawer,
  EVSvgIcon,
  EVIconButton,
  EVTooltip,
  EVList,
  EVListItem,
  EVListItemIcon,
  EVListItemText,
  EVChevronLeft,
  EVChevronRight,
  EVClickAwayListener,
  EVNotificationsNone,
  EVBadge,
  EVButton,
} from '@eagleview/ev-comp-library';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import UserProfile from 'commonComponents/UserProfileOld';
import ViewProfileDialog from 'features/view-profile-dialog';
import isEmpty from 'lodash/isEmpty';
import NotificationPopup from 'features/notifications-popup-old';
import { buildEvoneOauthLoginUrl } from 'utils/app.utils';
import { navigationBlockerAction, navigationUnBlockerAction, setPreviousPathAction } from '../../features/side-bar/SideBar.actions';
import useStyles from './PrimaryNavBarStyle';
import {
  IS_NOTIFS_TRANSPORT_SERVICE_ENABLED, LOGO_TYPE, HIDDEN_APPS,
} from '../../constants/AppConstants';
import EVLogo from '../EVLogo';
import EvoneIcon from './EvoneIcon';
import EvoneWideIcon from './EvoneWideIcon';

/**
 * PrimaryNavBar Component - renders list of applications
 */
const PrimaryNavBar = (props) => {
  const {
    apps, defaultAppId, handleNavChange, userDetails, msgsCount, navigationBlockerFlag, navigationUnBlockerFlag, previousPath, show,
    initiatorDetails, isImpersonated, isSignOutDisabled, userProfileError, isGuestUser, evoneBannerVisible,
    thirdPartyAppList,
  } = props;
  const pathname = (window.location && window.location.pathname) || '/';
  const appIdInUrl = pathname.split('/')[1] || '';
  const [selectedApp, setSelectedApp] = useState(appIdInUrl);
  const [notificationPanelOpen, setnotificationPanelOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const classes = useStyles({ open, show });
  const [viewProfile, setViewProfile] = useState(false);
  const [hideNotificationsBadge, setHideNotificationsBadge] = useState(true);
  const [tooltipOpen, setTooltipOpen] = React.useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEmpty(userDetails)) {
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
    }
  }, [userDetails]);

  useEffect(() => {
    if (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES') {
      setHideNotificationsBadge(msgsCount <= 0);
    }
  }, [msgsCount]);

  /**
   * Toggles between collapsed and expanded state
   */
  const handleDrawerOption = () => (open ? setOpen(false) : setOpen(true));

  /**
   * sets the state to collapsed when clicked outside PrimaryNavBar
   */
  const handleDrawerClickAway = () => {
    setOpen(false);
  };

  /**
   * hides the notifications-panel when clicked outside it
   */
  const handleNotificationClickAway = () => {
    setnotificationPanelOpen(false);
  };

  /**
   * useEffect called when unsaved changes are there
   * and it will revert viewpoint app selection
   */
  useEffect(() => {
    if (navigationBlockerFlag) {
      const appId = pathname.trim().split('/')[1];
      if (selectedApp !== appId) {
        dispatch(setPreviousPathAction({ prevPath: selectedApp }));
        setSelectedApp(appId);
        // eslint-disable-next-line no-unused-expressions
        handleNavChange && handleNavChange(appId);
      }
      dispatch(navigationBlockerAction());
    }
  }, [navigationBlockerFlag]);

  /**
   * useEffect called when user unblock navigation
   * and its highlight next location app
   */
  useEffect(() => {
    if (navigationUnBlockerFlag) {
      setSelectedApp(previousPath);
      // eslint-disable-next-line no-unused-expressions
      handleNavChange && handleNavChange(previousPath);
      dispatch(navigationUnBlockerAction());
    }
  }, [navigationUnBlockerFlag]);

  /**
   * called when user clicks on one of the applications.
   * updates selected appId to state and calls handleNavChange function passed through props
   * @param {Object} app - application selected
   */
  const handleClick = (app) => {
    const appId = app.appId || app;
    if (open) {
      setOpen(false);
    }
    if (notificationPanelOpen) {
      setnotificationPanelOpen(false);
    }
    if (app.externalLink) {
      window.open(app.rootUrl, '_blank');
    } else {
      setSelectedApp(appId);
      // eslint-disable-next-line no-unused-expressions
      handleNavChange && handleNavChange(appId);
    }
  };

  const handleShowNotifications = () => {
    if (open) {
      setOpen(false);
    }
    setnotificationPanelOpen(!notificationPanelOpen);
  };

  const handleEvoneButtonClick = () => {
    // #genai pass login_hint so auth-service can pre-fill / skip the sign-in form for the current user
    const url = buildEvoneOauthLoginUrl(thirdPartyAppList, userDetails?.userName);
    if (url) {
      window.location.assign(url);
    }
  };

  /**
   * generates svg html element from svg string
   * @param {Object} app - application selected
   * @param {String} app.icon - svg icon in string format
   * @param {String} appSelected - currently selected application id
   */
  const getSVG = (app, appSelected) => {
    const svgIcon = app.icon;
    try {
      const innerHtml = new DOMParser().parseFromString(svgIcon, 'text/html').body.firstChild.innerHTML;
      return (
        <svg
          className={clsx({ [classes.appIcon]: true }, { [classes.appIconSelected]: app.appId === appSelected })}
          dangerouslySetInnerHTML={{ __html: innerHtml }}
        />
      );
    } catch (e) { return null; }
  };

  const filteredApps = apps.filter((app) => !HIDDEN_APPS.includes(app.appId));

  // calls handleClick function to set default application when none selected
  if (defaultAppId && selectedApp === '') handleClick(defaultAppId);
  return (
    <>
      <EVClickAwayListener onClickAway={handleDrawerClickAway}>
        <EVDrawer
          variant="permanent"
          id="drawerMenuIcon"
          classes={{
            paper: clsx(classes.drawer, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
          open={open}
        >
          <div className={classes.logoContent}>
            <EVLogo
              type={open ? LOGO_TYPE.LOGO_WITH_TEXT_STACKED : LOGO_TYPE.LOGO}
              id="evLogoIcon"
              className={clsx({ [classes.logo]: true }, { [classes.logoWithText]: open })}
            />
            {evoneBannerVisible && (
            <EVButton
              type="button"
              id="evoneButton"
              className={clsx(classes.evoneButton, open && classes.evoneButtonOpen)}
              onClick={handleEvoneButtonClick}
              aria-label="Evone"
            >
              {open
                ? <EvoneWideIcon className={classes.evoneButtonImage} />
                : <EvoneIcon className={classes.evoneButtonImage} />}
            </EVButton>
            )}
          </div>
          <UserProfile
            setViewProfile={setViewProfile}
            monogramVariant={open ? 'medium' : 'small'}
            userDetails={userDetails}
            withUserMenu
            initiatorDetails={initiatorDetails}
            isImpersonated={isImpersonated}
            isSignOutDisabled={isSignOutDisabled}
            userProfileError={userProfileError}
            isGuestUser={isGuestUser}
          />
          <ViewProfileDialog userDetails={userDetails} viewProfile={viewProfile} setViewProfile={setViewProfile} isImpersonated={isImpersonated} />
          <div className={classes.listContent}>
            <EVList
              classes={{ padding: classes.removeListPadding }}
              id="navBarList"
            >
              {filteredApps.map((app) => (
                <EVListItem
                  id={`listItem-${app.appId}`}
                  onClick={() => handleClick(app)}
                  key={app.appId}
                  disableGutters={!open}
                  className={clsx(
                    classes.listStylesBase,
                    open ? classes.listRowOpen : classes.listRowCollapsed,
                    { [classes.selectedAppStyle]: (app.appId === selectedApp) },
                  )}
                >
                  <EVTooltip
                    id={`listItemToolTip-${app.appId}`}
                    title={app.tooltip}
                    open={show && tooltipOpen === app.appId}
                    onClose={() => setTooltipOpen(null)}
                    onOpen={() => setTooltipOpen(app.appId)}
                  >
                    <EVListItemIcon className={classes.listItemIcon}>
                      <EVSvgIcon component={() => getSVG(app, selectedApp)} viewBox="0 0 244 244" />
                    </EVListItemIcon>
                  </EVTooltip>
                  {open && <EVListItemText className={classes.listItemText} primary={app.name} />}
                </EVListItem>
              ))}
            </EVList>
          </div>
          <>
            {!isGuestUser && (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES') && (
              <div
                className={clsx(
                  classes.notificationsButton,
                  open ? classes.notificationsButtonOpen : classes.notificationsButtonCollapsed,
                )}
              >
                <EVIconButton
                  onClick={() => handleShowNotifications()}
                  id="bellIcon"
                  className={clsx(
                    classes.listItemIcon, { [classes.selectedAppStyle]: false },
                  )}
                >
                  <EVBadge id="badgeIcon" variant="dot" color="secondary" invisible={hideNotificationsBadge}>
                    <EVNotificationsNone className={classes.chevronIcon} />
                  </EVBadge>
                  {open && <EVListItemText className={classes.listItemText} primary="Notifications" />}
                </EVIconButton>
              </div>
            )}
          </>
          <div
            className={clsx(
              classes.drawerHandlerButton,
              open ? classes.drawerHandlerButtonOpen : classes.drawerHandlerButtonCollapsed,
            )}
          >
            <EVIconButton id="chevronIconButton" onClick={handleDrawerOption}>
              {open
                ? <EVChevronLeft id="chevronLeft" className={classes.chevronIcon} />
                : <EVChevronRight id="chevronRight" className={classes.chevronIcon} />}
            </EVIconButton>
          </div>
        </EVDrawer>
      </EVClickAwayListener>
      <div>
        {!isGuestUser && notificationPanelOpen && (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES') && (
          <NotificationPopup id="notificationPanel" handleClickAway={handleNotificationClickAway} />
        )}
      </div>
    </>
  );
};

PrimaryNavBar.propTypes = {
  /**
   * The list of applications that are rendered.
   * @param {{appId: String, icon: String, tooltip: String, name: String, order: Number}[]} apps
   */
  apps: PropTypes.arrayOf(PropTypes.shape({
    appId: PropTypes.string,
    icon: PropTypes.string,
    tooltip: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
  })).isRequired,

  /**
   * This handler will be called every time an application is selected.
   * @param {String} appId - The selected application id.
   * @property {Function} handleNavChange
   */
  handleNavChange: PropTypes.func,

  /**
   * default application id
   * @param {String} defaultAppId
   */
  defaultAppId: PropTypes.string,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * Details of the initiator when impersonation is done
   * @property {Object} initiatorDetails
   */
  initiatorDetails: PropTypes.shape().isRequired,

  /**
   * store value indicating whether the user is impersonated or not
   * @property {Boolean} isImpersonated
   */
  isImpersonated: PropTypes.bool.isRequired,

  /**
   * No of unread notifications
   * @property {Object} msgsCount
   */
  msgsCount: PropTypes.number,
  /**
   * action blocker popup value
   * @property {Boolean} navigationBlockerFlag
   */
  navigationBlockerFlag: PropTypes.bool,
  /**
   * saveDontSave clicked value
   * @property {Boolean} navigationUnBlockerFlag
   */
  navigationUnBlockerFlag: PropTypes.bool,
  /**
   * previousPath for app selection
   * @property {Boolean} previousPath
   */
  previousPath: PropTypes.string,
  /**
   * show/hide
   * @property {Object} userDetails
   */
  show: PropTypes.bool.isRequired,
  /**
   * store value indicating whether the sign out button is disabled or not
   * @property {Boolean} isSignOutDisabled
   */
  isSignOutDisabled: PropTypes.bool.isRequired,

  /**
   * User profile error
   * @property {Boolean} userProfileError
   */
  userProfileError: PropTypes.bool,

  /**
   * Guest user (isResourceShared && !isEvUser); show "--" avatar and logout-only menu. #genai
   * @property {Boolean} isGuestUser
   */
  isGuestUser: PropTypes.bool,
  /**
   * Show Evone button when EVONE entitlements + showHorizonBanner flag. #genai
   * @property {Boolean} evoneBannerVisible
   */
  evoneBannerVisible: PropTypes.bool,
  /** #genai third-party entitled apps (EVONE appURL for banner redirect) */
  thirdPartyAppList: PropTypes.arrayOf(PropTypes.shape()),
};

PrimaryNavBar.defaultProps = {
  defaultAppId: '',
  handleNavChange: undefined,
  msgsCount: 0,
  navigationBlockerFlag: false,
  navigationUnBlockerFlag: false,
  previousPath: '',
  userProfileError: false,
  isGuestUser: false,
  evoneBannerVisible: false,
  thirdPartyAppList: [],
};

const selector = (state) => {
  const { isResourceShared, isEvUser } = state.GuestAccess || {};
  const isShareTokenFromState = Boolean(state.Authorize?.isShareToken);
  // #genai include isShareToken so guest avatar works on base paths without a shared resource
  const isGuestUser = Boolean((isResourceShared && !isEvUser) || isShareTokenFromState);
  return {
    navigationBlockerFlag: state.SideBar.navigationBlockerFlag,
    navigationUnBlockerFlag: state.SideBar.navigationUnBlockerFlag,
    previousPath: state.SideBar.previousPath,
    isSignOutDisabled: state.Authorize.isSignOutDisabled,
    isGuestUser,
    thirdPartyAppList: state.UserEntitledApps?.thirdPartyAppList || [],
  };
};

export default connect(selector)(PrimaryNavBar);
